import { gql, useQuery } from '@apollo/client';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import filterNullsAndUndefined from '@orn/shared-web/utils/filterNullsAndUndefined';
import formatCalendarDate from '@orn/shared-web/utils/formatCalendarDate';
import toCurrency from '@orn/shared-web/utils/toCurrency';

import Table from '../../components/Table';

import type {
  AdminGetTransactionPairsQuery,
  AdminGetTransactionPairsQueryVariables,
} from './__generated__/TransactionPairs.gql';

const ADMIN_GET_TRANSACTION_PAIRS = gql`
  query AdminGetTransactionPairs($familyId: ID!) {
    adminGetTransactionPairsForFamily(familyId: $familyId) {
      id
      pairType
      debitTransaction {
        id
        amount
        date
        description
        rawJson
        merchant {
          id
          name
        }
        financialAccount {
          id
          name
        }
      }
      creditTransaction {
        id
        amount
        date
        description
        rawJson
        merchant {
          id
          name
        }
        financialAccount {
          id
          name
        }
      }
    }
  }
`;

interface ITransactionPairsProps {
  familyId: string | null;
}

/**
 * Lists debit/credit transaction pairs detected for the family — an operator
 * sanity check that paired transactions are not double-counted as anomalies.
 * Renders nothing while loading or if there are no pairs.
 */
const TransactionPairs = ({ familyId }: ITransactionPairsProps) => {
  const { data, loading, error } = useQuery<AdminGetTransactionPairsQuery, AdminGetTransactionPairsQueryVariables>(
    ADMIN_GET_TRANSACTION_PAIRS,
    {
      variables: { familyId: familyId ?? '' },
      skip: !familyId,
    }
  );

  if (loading || error) {
    return null;
  }

  const pairs = data?.adminGetTransactionPairsForFamily ?? [];
  if (pairs.length === 0) {
    return null;
  }

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Pair type</Table.HeaderCell>
          <Table.HeaderCell>Debit</Table.HeaderCell>
          <Table.HeaderCell>Credit</Table.HeaderCell>
          <Table.HeaderCell className="text-right">Debit amount</Table.HeaderCell>
          <Table.HeaderCell className="text-right">Credit amount</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {pairs.map((pair) => {
          const debitAmount =
            typeof pair.debitTransaction.amount === 'string'
              ? parseFloat(pair.debitTransaction.amount)
              : pair.debitTransaction.amount;
          const creditAmount =
            typeof pair.creditTransaction.amount === 'string'
              ? parseFloat(pair.creditTransaction.amount)
              : pair.creditTransaction.amount;

          return (
            <Table.Row key={pair.id}>
              <Table.Cell>{pair.pairType}</Table.Cell>
              <Table.Cell>
                <PairSide side={pair.debitTransaction} />
              </Table.Cell>
              <Table.Cell>
                <PairSide side={pair.creditTransaction} />
              </Table.Cell>
              <Table.Cell mono className="text-right">
                {toCurrency(debitAmount)}
              </Table.Cell>
              <Table.Cell mono className="text-right">
                {toCurrency(creditAmount)}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

interface IPairSideProps {
  side: AdminGetTransactionPairsQuery['adminGetTransactionPairsForFamily'][number]['debitTransaction'];
}

const PairSide = ({ side }: IPairSideProps) => {
  const filteredJson = side.rawJson ? filterNullsAndUndefined(side.rawJson) : undefined;
  const dateLabel = formatCalendarDate(side.date, 'medium');
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-fg-primary text-sm">{side.description ?? 'N/A'}</span>
      <span className="text-fg-secondary flex flex-wrap gap-x-2 text-xs">
        {filteredJson ? (
          <Tooltip
            title="Transaction JSON"
            placement="right"
            wide
            description={
              <pre className="wrap-break-word whitespace-pre-wrap text-xs">{JSON.stringify(filteredJson, null, 2)}</pre>
            }
          >
            <TooltipTrigger>
              <span className="cursor-help underline decoration-dotted">{dateLabel}</span>
            </TooltipTrigger>
          </Tooltip>
        ) : (
          <span>{dateLabel}</span>
        )}
        <span>{side.financialAccount?.name ?? 'No account'}</span>
        {side.merchant?.name ? <span>{side.merchant.name}</span> : null}
        <span className="font-mono">{side.id.slice(0, 8)}…</span>
      </span>
    </div>
  );
};

export default TransactionPairs;
